import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

// Fichier source accepté avant traitement. Au-delà, le navigateur peinerait
// sur la lecture et le redimensionnement pour un gain nul.
const TAILLE_MAX_OCTETS = 2 * 1024 * 1024;

// Les icônes sont rendues dans un carré de 56 px. On redimensionne à 256 px
// pour rester net sur les écrans à forte densité, sans stocker en base une
// image de 4000 px qui alourdirait le chargement de tout le portail.
const COTE_MAX = 256;

const formeVide = {
  name: '', description: '', url: '', icon: '', iconType: 'base64',
  color: '#0F56E3', groupIds: [], order: 0, isActive: true
};

// Lit un fichier image et rend une data: URI prête à stocker.
const lireImage = (fichier) => new Promise((resolve, reject) => {
  if (!fichier.type.startsWith('image/')) return reject(new Error("Ce fichier n'est pas une image."));
  if (fichier.size > TAILLE_MAX_OCTETS) return reject(new Error('Image trop lourde (2 Mo maximum).'));

  const lecteur = new FileReader();
  lecteur.onerror = () => reject(new Error('Lecture du fichier impossible.'));
  lecteur.onload = () => {
    // Un SVG est conservé tel quel : le rastériser lui ferait perdre
    // précisément ce qui en fait l'intérêt.
    if (fichier.type === 'image/svg+xml') return resolve(lecteur.result);

    const img = new Image();
    img.onerror = () => reject(new Error('Image illisible ou format non pris en charge.'));
    img.onload = () => {
      const echelle = Math.min(1, COTE_MAX / Math.max(img.width, img.height));
      const largeur = Math.max(1, Math.round(img.width * echelle));
      const hauteur = Math.max(1, Math.round(img.height * echelle));
      const canvas = document.createElement('canvas');
      canvas.width = largeur;
      canvas.height = hauteur;
      canvas.getContext('2d').drawImage(img, 0, 0, largeur, hauteur);
      // PNG et non JPEG : les logos ont presque tous un fond transparent.
      resolve(canvas.toDataURL('image/png'));
    };
    img.src = lecteur.result;
  };
  lecteur.readAsDataURL(fichier);
});

export default function AppFormModal({ app, groups, onClose, onSaved }) {
  const [form, setForm] = useState(app
    ? { ...formeVide, ...app, groupIds: (app.groups || []).map(g => g.id) }
    : formeVide);
  const [msg, setMsg] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [survol, setSurvol] = useState(false);
  const champFichier = useRef(null);

  const maj = (patch) => setForm(f => ({ ...f, ...patch }));

  // Échap ferme, comme dans toute boîte de dialogue.
  useEffect(() => {
    const surTouche = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onClose]);

  const basculerGroupe = (groupId) => setForm(f => ({
    ...f,
    groupIds: f.groupIds?.includes(groupId)
      ? f.groupIds.filter(id => id !== groupId)
      : [...(f.groupIds || []), groupId]
  }));

  const traiterFichier = async (fichier) => {
    if (!fichier) return;
    try {
      maj({ icon: await lireImage(fichier), iconType: 'base64' });
      setMsg('');
    } catch (e) {
      setMsg(`Erreur : ${e.message}`);
    }
  };

  const enregistrer = async () => {
    setEnCours(true);
    setMsg('');
    try {
      if (app) await api.put(`/applications/admin/${app.id}`, form);
      else await api.post('/applications/admin', form);
      onSaved();
      onClose();
    } catch (e) {
      setMsg(`Erreur : ${e.response?.data?.message || e.message}`);
      setEnCours(false);
    }
  };

  const aUneImage = form.iconType === 'base64' && form.icon;

  return (
    <div style={styles.overlay} onClick={onClose}>
      {/* Le clic sur le fond ferme ; celui sur la boîte ne doit pas remonter. */}
      <div style={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={styles.modalHeader}>
          <h3>{app ? "Modifier l'application" : 'Nouvelle application'}</h3>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Fermer">✕</button>
        </div>

        <div style={styles.modalBody}>
          {msg && <div style={styles.erreur}>{msg}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Nom *</label>
            <input value={form.name || ''} onChange={e => maj({ name: e.target.value })} style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Description</label>
            <input value={form.description || ''} onChange={e => maj({ description: e.target.value })} style={styles.input} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>URL *</label>
            <input value={form.url || ''} onChange={e => maj({ url: e.target.value })} placeholder="https://..." style={styles.input} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Icône</label>
            <div className="icone-modes">
              {[['base64', 'Image'], ['url', 'URL'], ['emoji', 'Emoji']].map(([valeur, libelle]) => (
                <button
                  key={valeur}
                  type="button"
                  className={`icone-mode ${form.iconType === valeur ? 'is-active' : ''}`}
                  onClick={() => { if (form.iconType !== valeur) maj({ iconType: valeur, icon: '' }); }}
                >
                  {libelle}
                </button>
              ))}
            </div>

            {form.iconType === 'base64' && (
              <>
                <div
                  className={`dropzone ${survol ? 'is-survol' : ''}`}
                  onDragOver={e => { e.preventDefault(); setSurvol(true); }}
                  onDragLeave={() => setSurvol(false)}
                  onDrop={e => { e.preventDefault(); setSurvol(false); traiterFichier(e.dataTransfer.files?.[0]); }}
                  onClick={() => champFichier.current?.click()}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); champFichier.current?.click(); } }}
                  role="button"
                  tabIndex={0}
                  aria-label="Déposer ou choisir une image d'icône"
                >
                  <input
                    ref={champFichier}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                    style={{ display: 'none' }}
                    onChange={e => { traiterFichier(e.target.files?.[0]); e.target.value = ''; }}
                  />
                  {aUneImage ? (
                    <>
                      <img src={form.icon} alt="" className="dropzone-apercu" />
                      <span className="dropzone-texte">Cliquez ou déposez pour remplacer</span>
                    </>
                  ) : (
                    <>
                      <span className="dropzone-glyphe" aria-hidden="true">🖼</span>
                      <span className="dropzone-texte">Glissez une image ici, ou cliquez pour choisir</span>
                      <span className="dropzone-detail">PNG, JPG, SVG, WebP — redimensionnée à {COTE_MAX} px</span>
                    </>
                  )}
                </div>
                {aUneImage && (
                  <button type="button" className="dropzone-retirer" onClick={() => maj({ icon: '' })}>
                    Retirer l'icône
                  </button>
                )}
              </>
            )}

            {form.iconType === 'url' && (
              <input
                value={form.icon || ''}
                onChange={e => maj({ icon: e.target.value })}
                placeholder="https://exemple.fr/logo.png"
                style={styles.input}
              />
            )}

            {form.iconType === 'emoji' && (
              <input
                value={form.icon || ''}
                onChange={e => maj({ icon: e.target.value })}
                placeholder="🚀"
                maxLength={8}
                style={styles.input}
              />
            )}
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Couleur de fond</label>
              <input type="color" value={form.color || '#0F56E3'} onChange={e => maj({ color: e.target.value })} style={{ ...styles.input, height: 40, padding: 4 }} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Ordre</label>
              <input type="number" value={form.order || 0} onChange={e => maj({ order: parseInt(e.target.value, 10) || 0 })} style={styles.input} />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Pôles</label>
            <p style={styles.hint}>Tous les membres des pôles cochés auront accès à cet outil.</p>
            <div style={styles.checkList}>
              {groups.map(g => (
                <label key={g.id} style={styles.checkRow}>
                  <input type="checkbox" checked={form.groupIds?.includes(g.id) || false} onChange={() => basculerGroupe(g.id)} />
                  {g.icon && <span>{g.icon}</span>}
                  <span style={{ fontSize: 14 }}>{g.name}</span>
                </label>
              ))}
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => maj({ isActive: e.target.checked })} />
            Application active
          </label>
        </div>

        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>Annuler</button>
          <button onClick={enregistrer} disabled={enCours} style={styles.saveBtn}>
            {enCours ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f4f6f9' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#5f6673' },
  modalBody: { padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #f4f6f9' },
  erreur: { background: '#fff0ed', color: '#E46138', border: '1px solid #E46138', borderRadius: 8, padding: '10px 14px', fontSize: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  label: { fontSize: 13, fontWeight: 500, color: '#1a1a2e' },
  hint: { fontSize: 12, color: '#5f6673' },
  input: { padding: '10px 12px', borderRadius: 8, border: '1.5px solid #D9D9D9', fontSize: 14, color: '#1a1a2e', width: '100%' },
  row: { display: 'flex', gap: 16 },
  checkList: { display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 200, overflowY: 'auto', border: '1.5px solid #D9D9D9', borderRadius: 8, padding: 10 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' },
  cancelBtn: { background: '#f4f6f9', color: '#5f6673', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  saveBtn: { background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
